import React, { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { getTimestamp } from '../../shared/utils/helpers';
import Modal from '../../shared/components/Modal';
import Field from '../../shared/components/Field';
import { uploadToStorage } from '../../../config/supabase';
import { Upload, FileText, Trash2, Loader2 } from 'lucide-react';

const MAINTENANCE_TYPES = [
  { key: 'Vidange', fr: 'Vidange d\'huile', ar: 'تغيير الزيت' },
  { key: 'Filtre à huile', fr: 'Filtre à huile', ar: 'فلتر الزيت' },
  { key: 'Filtre à air', fr: 'Filtre à air', ar: 'فلتر الهواء' },
  { key: 'Filtre à carburant', fr: 'Filtre à carburant', ar: 'فلتر الوقود' },
  { key: 'Courroie de distribution', fr: 'Courroie de distribution', ar: 'حزام التوقيت' },
  { key: 'Pneus', fr: 'Pneus', ar: 'الإطارات' },
  { key: 'Freins', fr: 'Freins', ar: 'الفرامل' },
  { key: 'Bougies', fr: 'Bougies', ar: 'الشمعات' },
  { key: 'Batterie', fr: 'Batterie', ar: 'البطارية' },
  { key: 'Climatisation', fr: 'Climatisation', ar: 'التكييف' },
  { key: 'Autre', fr: 'Autre', ar: 'أخرى' }
];

export default function VidangeForm({ cars, settings, onSave, onClose }) {
  const { lang, t } = useLanguage();
  const isAr = lang === 'ar';

  const [data, setData] = useState({
    carId: '', 
    carName: '', 
    date: new Date().toISOString().split('T')[0],
    currentKm: 0, 
    nextKm: 0, 
    nextDate: '',
    cost: 0, 
    type: 'Vidange', 
    parts: '',
    notes: '',
    invoiceUrl: '',
    invoiceName: ''
  });

  const [uploading, setUploading] = useState(false);

  const getDefaultInterval = (type) => {
    const intervals = settings?.maintIntervals || {};
    if (intervals[type] !== undefined) return parseFloat(intervals[type]) || 10000;
    switch(type) {
      case 'Vidange':
      case 'Filtre à huile': return 10000;
      case 'Filtre à air':
      case 'Climatisation': return 20000;
      case 'Filtre à carburant':
      case 'Freins':
      case 'Bougies': return 30000;
      case 'Pneus': return 40000;
      case 'Batterie': return 60000;
      case 'Courroie de distribution': return 80000;
      default: return 10000;
    }
  };

  const handleCarChange = (carId) => {
    const c = cars.find(x => x.id === carId);
    if (!c) {
      setData(prev => ({ ...prev, carId: '', carName: '', currentKm: 0, nextKm: 0 }));
      return;
    }

    const currentKm = Math.round((parseFloat(c.km) || 0) + (parseFloat(c.gpsOdometer) || 0));
    const interval = getDefaultInterval(data.type);

    setData(prev => ({
      ...prev,
      carId,
      carName: `${c.brand} ${c.model} (${c.plate})`,
      currentKm,
      nextKm: currentKm + interval
    }));
  };

  const handleTypeChange = (type) => {
    const interval = getDefaultInterval(type);
    setData(prev => ({
      ...prev,
      type,
      nextKm: prev.currentKm + interval
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadToStorage(file, 'maintenance_invoices');
      setData(prev => ({ ...prev, invoiceUrl: url, invoiceName: file.name }));
    } catch (err) {
      alert('❌ ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!data.carId) return alert(t('choose_car') || (isAr ? 'يرجى اختيار السيارة' : 'Veuillez choisir le véhicule'));
    
    try {
      await onSave({
        ...data,
        currentKm: parseInt(data.currentKm) || 0,
        nextKm: parseInt(data.nextKm) || 0,
        cost: parseFloat(data.cost) || 0,
        createdAt: getTimestamp()
      });
      onClose();
    } catch (err) {
      alert('❌ ' + err.message);
    }
  };

  return (
    <Modal onClose={onClose} title={isAr ? 'تسجيل صيانة جديدة' : 'Nouvel Entretien'}>
      <div className="space-y-4 max-h-[75vh] overflow-y-auto px-1">
        {/* Car Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('car_req')}</label>
          <select 
            value={data.carId}
            onChange={e => handleCarChange(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">{t('choose_car')}</option>
            {cars.map(c => (
              <option key={c.id} value={c.id}>{`${c.brand} ${c.model} - ${c.plate}`}</option>
            ))}
          </select>
        </div>

        {/* Date and Type */}
        <div className="grid grid-cols-2 gap-4">
          <Field 
            label={t('col_date')} 
            type="date" 
            value={data.date} 
            onChange={v => setData(prev => ({ ...prev, date: v }))} 
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('type_label') || (isAr ? 'النوع' : 'Type')}</label>
            <select 
              value={data.type} 
              onChange={e => handleTypeChange(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
            >
              {MAINTENANCE_TYPES.map(t => (
                <option key={t.key} value={t.key}>{isAr ? t.ar : t.fr}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Mileage inputs */}
        <div className="grid grid-cols-2 gap-4">
          <Field 
            label={t('col_km_current') + ' (km)'} 
            type="number" 
            value={data.currentKm} 
            onChange={v => setData(prev => ({ ...prev, currentKm: v }))} 
          />
          <Field 
            label={t('col_km_next') + ' (km)'} 
            type="number" 
            value={data.nextKm} 
            onChange={v => setData(prev => ({ ...prev, nextKm: v }))} 
          />
        </div>

        {/* Next Scheduled Date and Cost */}
        <div className="grid grid-cols-2 gap-4">
          <Field 
            label={isAr ? 'تاريخ الاستحقاق القادم' : 'Prochaine date prévue'} 
            type="date" 
            value={data.nextDate} 
            onChange={v => setData(prev => ({ ...prev, nextDate: v }))} 
          />
          <Field 
            label={`${t('col_cost')} (${settings.currency})`} 
            type="number" 
            value={data.cost} 
            onChange={v => setData(prev => ({ ...prev, cost: v }))} 
          />
        </div>

        {/* Replaced Parts */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            ⚙️ {isAr ? 'قطع الغيار المستبدلة' : 'Pièces remplacées'}
          </label>
          <input 
            type="text"
            value={data.parts}
            onChange={e => setData(prev => ({ ...prev, parts: e.target.value }))}
            placeholder={isAr ? 'مثال: فحمات الفرامل، زيت المحرك 5w30، فلتر الهواء...' : 'Ex: Plaquettes de frein, Huile 5w30, Filtre à air...'}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('col_notes')}</label>
          <textarea 
            value={data.notes} 
            onChange={e => setData(prev => ({ ...prev, notes: e.target.value }))}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 font-sans text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
            rows={2} 
            placeholder={isAr ? 'ملاحظات إضافية...' : 'Remarques supplémentaires...'}
          />
        </div>

        {/* Invoice File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            📄 {isAr ? 'تحميل الفاتورة / المستند' : 'Facture / Document joint'}
          </label>
          <div className="flex items-center gap-3 mt-1">
            {data.invoiceUrl ? (
              <div className="flex items-center justify-between border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20 px-3 py-2 rounded-lg text-xs w-full">
                <div className="flex items-center gap-2 text-emerald-800 dark:text-emerald-300 min-w-0">
                  <FileText className="w-4 h-4 shrink-0" />
                  <span className="truncate font-semibold">{data.invoiceName || 'invoice.pdf'}</span>
                </div>
                <button 
                  onClick={() => setData(prev => ({ ...prev, invoiceUrl: '', invoiceName: '' }))}
                  className="text-red-500 hover:text-red-700 p-1 rounded transition"
                  type="button"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition text-gray-500">
                {uploading ? (
                  <div className="flex flex-col items-center gap-1.5">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                    <span className="text-xs">{isAr ? 'جاري الرفع...' : 'Téléchargement...'}</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1.5">
                    <Upload className="w-5 h-5 text-gray-400" />
                    <span className="text-xs font-medium">{isAr ? 'اختر ملفاً (PDF, صور)' : 'Sélectionner un fichier (PDF, Images)'}</span>
                  </div>
                )}
                <input 
                  type="file" 
                  accept="image/*,application/pdf" 
                  onChange={handleFileChange} 
                  className="hidden" 
                  disabled={uploading}
                />
              </label>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6 border-t border-gray-150 dark:border-gray-700 pt-4">
          <button 
            type="button"
            onClick={onClose} 
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm transition"
          >
            {t('cancel_btn')}
          </button>
          <button 
            type="button"
            onClick={submit} 
            disabled={uploading}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold shadow transition text-sm disabled:opacity-50"
          >
            {t('save_btn') || (isAr ? 'حفظ' : 'Enregistrer')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
